// Low-level fetch used by both the legacy `useRequest` hook and the new
// TanStack Query wrappers (`useApiQuery` / `useApiMutation`).
//
// It does NOT decide success vs. failure — it returns `{ body, status }` for
// any HTTP response and only throws on transport-level failures (network
// error, abort). The throw-on-non-2xx contract lives in the wrappers so the
// legacy hook can keep its existing tuple/no-throw behavior.

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiRequestOptions {
  endpoint: string;
  method?: HttpMethod;
  data?: any;
  params?: Record<string, any>;
  pathParam?: string;
  headers?: Record<string, any>;
  signal?: AbortSignal;
}

export interface ApiResponse<T = any> {
  body: T;
  status: number;
}

export class ApiError<T = any> extends Error {
  status: number;
  data: T;
  constructor(status: number, data: T) {
    const message =
      (data as any)?.message ||
      (data as any)?.error ||
      "An error occurred, please try again";
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

// Success = 2xx (covers 200/201/etc.) plus 304 Not Modified (cached).
export function isSuccessStatus(status: number): boolean {
  return (status >= 200 && status < 300) || status === 304;
}

const baseURL =
  process.env.REACT_APP_BACKEND_URL || "https://staging-api.vendstash.com";

function buildUrl(
  endpoint: string,
  params?: Record<string, any>,
  pathParam?: string,
): string {
  const queryParams = new URLSearchParams(params).toString();
  if (pathParam && queryParams)
    return `${baseURL}${endpoint}/${pathParam}?${queryParams}`;
  if (pathParam) return `${baseURL}${endpoint}/${pathParam}`;
  if (queryParams) return `${baseURL}${endpoint}?${queryParams}`;
  return `${baseURL}${endpoint}`;
}

export async function apiRequest<T = any>({
  endpoint,
  method = "GET",
  data,
  params,
  pathParam,
  headers,
  signal,
}: ApiRequestOptions): Promise<ApiResponse<T>> {
  const token = localStorage.getItem("token");
  const url = buildUrl(endpoint, params, pathParam);

  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...headers,
    },
    body: method !== "GET" ? JSON.stringify(data) : undefined,
    signal,
  });

  // Guard empty/non-JSON bodies (e.g. 204/304) so parsing never throws.
  const text = await response.text();
  let body: T;
  try {
    body = (text ? JSON.parse(text) : {}) as T;
  } catch {
    body = {} as T;
  }
  return { body, status: response.status };
}
